import moment from "moment";
import type { TransactionWithCategory } from "../chartDataFunctions";
import {
  generateShortAccountNumber,
  getDateByFuncType,
  getTransactionOriginalAmountLabel,
} from "../chartDataFunctions";
import {
  openFinanceProviderToNativeLanguageName,
  openFinanceProviderToProviderType,
} from "~/components/product/openfinance/openfinanceProviders";
import { useLanguage } from "~/hooks/useLanguage";
import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { cashflowDateType } from "../cashflowEnums";
import { Tooltip } from "~/components/ui";
import InfoIcon from "@mui/icons-material/Info";
import { BRAND_SECONDARY_RGB } from "~/brandColors";

interface TransactionDetailsProps {
  transaction: TransactionWithCategory;
  onConfirmDuplicate?: (
    transaction: TransactionWithCategory
  ) => void | Promise<void>;
  onRevokeConfirmDuplicate?: (
    transaction: TransactionWithCategory
  ) => void | Promise<void>;
  busy?: boolean;
}

export const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  transaction,
  onConfirmDuplicate,
  onRevokeConfirmDuplicate,
  busy = false,
}) => {
  const { lang } = useLanguage();
  const cf = cashflowCharts[lang];
  const dateRefFunctionType =
    window.localStorage.getItem("dateType") ===
    cashflowDateType.TRANSACTION_DATE
      ? cashflowDateType.TRANSACTION_DATE
      : cashflowDateType.VALUE_DATE;
  const shouldShowBankConnectedCardWarning =
    transaction.type === "CARD" &&
    openFinanceProviderToProviderType(transaction.providerId) === "BANK";

  const mainDescription = transaction.description?.description || "";
  const secondaryDescription = transaction.transactionDescription || "";
  const originalAmountLabel = getTransactionOriginalAmountLabel(transaction);

  return (
    <div className="flex w-full min-w-0">
      {/* Amount / Date / Account — first child → RIGHT in RTL.
          border-e = left border in RTL = the separator between amount and description. */}
      <div className="me-5 shrink-0 space-y-0.5 border-e border-border-subtle pe-5 mobile:me-2 mobile:pe-2">
        <div
          className={
            "text-2xl font-bold leading-8" +
            (transaction.ignored || transaction.autoDuplicate
              ? " line-through"
              : "")
          }
        >
          {transaction.valueString}
        </div>
        {originalAmountLabel ? (
          <div className="text-sm font-medium leading-5 text-gray-500">
            {originalAmountLabel}
          </div>
        ) : null}
        <div className="text-sm font-normal leading-5 text-gray-600">
          {moment(getDateByFuncType(dateRefFunctionType, transaction)).format(
            "DD.MM.YY"
          )}
        </div>
        <div className="text-sm font-semibold leading-5">
          <span className="text-xs font-normal text-gray-500">
            {generateShortAccountNumber(transaction.accountNumber)}{" "}
          </span>
          <span className="inline-flex items-center gap-1">
            <span>
              {openFinanceProviderToNativeLanguageName(transaction.providerId)}
            </span>
            {shouldShowBankConnectedCardWarning && (
              <Tooltip
                content={cashflowCharts[lang].bankConnectedCardWarning}
                className="inline-flex align-middle"
              >
                <button
                  type="button"
                  aria-label={cashflowCharts[lang].bankConnectedCardWarning}
                  className="inline-flex items-center p-0 focus-visible:outline-none"
                >
                  <InfoIcon
                    sx={{ fontSize: 16, color: "rgba(239, 68, 68, 0.75)" }}
                  />
                </button>
              </Tooltip>
            )}
          </span>
        </div>
      </div>

      {/* Description — second child → LEFT/CENTER in RTL */}
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center py-1">
        {mainDescription ? (
          <div className="text-center text-xl font-semibold leading-7 text-gray-800">
            {mainDescription}
          </div>
        ) : secondaryDescription ? (
          <div className="text-center text-lg font-medium leading-6 text-gray-600">
            {secondaryDescription}
          </div>
        ) : null}

        {mainDescription && secondaryDescription && (
          <div className="mt-1 text-center text-xs font-normal leading-4 text-gray-400">
            {secondaryDescription}
          </div>
        )}

        {/* Status callouts */}
        {transaction.duplicateInChecksumGroup ? (
          <div className="border-secondary/35 mt-2 flex w-full items-center gap-2 rounded-lg border bg-secondary/[0.07] p-2">
            <div className="flex-1 space-y-0.5 text-start font-Rubik">
              <div className="text-xs font-semibold leading-snug text-secondary">
                {cf.duplicateTransactionShort}
                <span className="ms-1 font-medium text-menu">
                  {" · "}
                  {transaction.duplicateConfirmedAsReal
                    ? cf.duplicateMarkedRealLine
                    : cf.duplicateNotIncludedInCashflow}
                </span>
              </div>
              <p className="text-[11px] leading-snug text-gray-600">
                {transaction.duplicateConfirmedAsReal
                  ? cf.duplicatePressCheckToUndoReal
                  : cf.duplicateConfirmCheckHint}
              </p>
            </div>
            {(onConfirmDuplicate || onRevokeConfirmDuplicate) && (
              <button
                type="button"
                className="shrink-0 rounded-lg p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                disabled={busy}
                aria-label={
                  transaction.duplicateConfirmedAsReal
                    ? cf.duplicateUndoIncludeButton
                    : cf.duplicateIncludeInFlowButton
                }
                aria-pressed={Boolean(transaction.duplicateConfirmedAsReal)}
                onClick={() => {
                  if (busy) return;
                  if (transaction.duplicateConfirmedAsReal) {
                    void onRevokeConfirmDuplicate?.(transaction);
                  } else {
                    void onConfirmDuplicate?.(transaction);
                  }
                }}
              >
                {busy ? (
                  <svg
                    className="h-5 w-5 animate-spin fill-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z"
                      opacity="0.8"
                    />
                  </svg>
                ) : (
                  <svg
                    className={
                      "h-5 w-5 " +
                      (transaction.duplicateConfirmedAsReal
                        ? "fill-secondary hover:fill-secondary/80"
                        : "fill-gray-300 hover:fill-secondary")
                    }
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    aria-hidden
                  >
                    <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : transaction.ignored && transaction.connectedCard ? (
          <div
            className="mt-2 w-full rounded-lg border p-2"
            style={{
              backgroundColor: "rgb(var(--color-secondary) / 0.07)",
              borderColor: "rgb(var(--color-secondary) / 0.35)",
            }}
          >
            <div className="space-y-0.5 text-start font-Rubik">
              <div
                className="text-xs font-semibold leading-snug"
                style={{ color: BRAND_SECONDARY_RGB }}
              >
                {cf.connectedCardShort}
                <span className="ms-1 font-medium text-menu">
                  {" · "}
                  {cf.connectedCardNotIncludedInCashflow}
                </span>
              </div>
              <p className="text-[11px] leading-snug text-gray-600">
                {cf.connectedCardHint}
              </p>
            </div>
          </div>
        ) : transaction.ignored && !transaction.duplicateInChecksumGroup ? (
          <div className="mt-2 w-full rounded-lg border border-border-subtle bg-surface-subtle p-2">
            <div className="space-y-0.5 text-start font-Rubik">
              <div className="text-xs font-semibold leading-snug text-gray-600">
                {cf.ignoredByRuleShort}
                <span className="ms-1 font-medium text-menu">
                  {" · "}
                  {cf.ignoredByRuleNotIncludedInCashflow}
                </span>
              </div>
              <p className="text-[11px] leading-snug text-gray-500">
                {cf.ignoredByRuleHint}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
